<template>
  <div class="regional-search regional-search__container">
    <span class="regional-search__input-container">
      <InputText
        :placeholder="props.searchLabel"
        class="regional-search__input-text"
        :model-value="inputSearch"
        @update:model-value="updateSearch"
      />
      <Button
        v-if="!!valueSearch"
        class="regional-search__input-close-button"
        severity="secondary"
        @click="resetSearch"
      >
        <span class="material-icons">close</span>
      </Button>
    </span>
    <span class="regional-search__input-container">
      <Dropdown
        v-if="!!props.highwaySelectionOptions?.length"
        class="regional-search__combo-box"
        :model-value="valueHighway"
        :options="props.highwaySelectionOptions"
        option-label="highway"
        option-value="highway"
        :placeholder="highwayLabel"
        show-clear
        @update:model-value="selectHighway"
      />
    </span>
    <span class="regional-search__input-container">
      <TownSelection
        v-if="props.townSelection"
        ref="townSelector"
        class="regional-search__combo-box"
        :town-label="props.townLabel"
        selector-type="autocomplete"
        @select-town="selectTown"
      />
    </span>
    <span class="regional-search__input-container">
      <Dropdown
        v-if="props.regionSelection"
        class="regional-search__combo-box"
        :model-value="valueRegion"
        :options="validRegions"
        :disabled="!!valueProvince"
        option-label="name"
        option-value="name"
        :placeholder="props.regionLabel"
        show-clear
        @update:model-value="selectRegion"
      />
    </span>
    <span class="regional-search__input-container">
      <Dropdown
        v-if="props.provinceSelection"
        class="regional-search__combo-box"
        :model-value="valueProvince"
        :options="validProvinces"
        option-label="name"
        option-value="name"
        :placeholder="props.provinceLabel"
        show-clear
        @update:model-value="selectProvince"
      />
    </span>
  </div>
</template>

<script setup>
import {ref, computed, watch} from 'vue';
import Dropdown from 'primevue/dropdown';
import TownSelection from '@/components/town-selection/TownSelection';
import InputText from 'primevue/inputtext';
import Button from 'primevue/button';
import {REGIONAL_SEARCH_PROPS} from './regional-search-props';

// #region Props, Emits
const props = defineProps({
  ...REGIONAL_SEARCH_PROPS,
  regions: {
    type: Array,
    default: () => [],
  },
  provinces: {
    type: Array,
    default: () => [],
  },
  modelValue: {
    type: Object,
    default: () => {
      return {
        search: null,
        town: null,
        region: null,
        province: null,
      };
    },
  },
});
const emit = defineEmits(['update:modelValue']);
// #endregion

// #region Params, Ref, Reactives, Computed
const townSelector = ref(null);
const valueSearch = computed(() =>
  props?.modelValue?.search);
const valueHighway = computed(() =>
  props?.modelValue?.highway);
const valueTown = computed(() =>
  props?.modelValue?.town);
const valueRegion = computed(() =>
  props?.modelValue?.region);
const valueProvince = computed(() =>
  props?.modelValue?.province);

const inputSearch = ref('');
const timeout = ref(null);
const validRegions = computed(() => {
  return props.regions;
});
const validProvinces = computed(() => {
  if (!!valueRegion.value) {
    return props.provinces.filter((p) => p.region === valueRegion.value);
  }
  return props.provinces;
});

// #region Functions
function resetSearch() {
  inputSearch.value = null;
  onChange({search: null});
}
function updateSearch(search) {
  if (!!timeout?.value) {
    clearTimeout(timeout.value);
  }
  timeout.value = setTimeout(() => {
    onChange({search});
  }, 500);
}
function selectHighway(highway) {
  onChange({highway});
}
function selectTown(town) {
  // townInput.value = town.value.name;
  onChange({town});
}
function selectRegion(region) {
  onChange({region});
}
function selectProvince(province) {
  onChange({province});
}
function onChange(data) {
  emit('update:modelValue', data);
}
// #endregion

// #region Effects, Hooks
watch(valueSearch, (newValueSearch) => {
  if (!newValueSearch) { // Clear search
    inputSearch.value = null;
  }
});
watch(valueTown, (newValueTown) => {
  if (!newValueTown) {
    townSelector.value.clearFilter();
  }
});
// #endregion

// #region Provide, Expose

// #endregion
</script>

<style lang="scss" scoped>
.regional-search {
  &__container {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
  }
  &__combo-box {
    margin: 0 0.5rem;
    min-width: 15rem;
  }
  &__input-container {
    position: relative;
  }
  &__input-close-button {
    border-radius: 100%;
    margin-right: 0.2rem;
    padding: 0.2rem;
    position: absolute;
    right: 0;
    top: 50%;
    transform: translateY(-50%);
    z-index: 1;
    & .material-icons {
      font-size: 1rem;
    }
  }
}
</style>
